import React, { useEffect, useState } from "react";
import { LogOut, User, ChevronDown, Wifi, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import { readEmbeddedBuildMeta } from "../../../lib/deployBuildMeta";
import { useHeader } from "../context/HeaderContext";

const UserActions: React.FC = () => {
  const { logout, user } = useAuth();
  const { getInitials } = useHeader();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [buildVersion, setBuildVersion] = useState<string | null>(
    () => readEmbeddedBuildMeta()?.version ?? null
  );

  useEffect(() => {
    if (buildVersion) return;
    let cancelled = false;
    void fetch(`/deploy-version.json?t=${Date.now()}`, { cache: "no-store" })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { version?: string } | null) => {
        if (!cancelled && data?.version) setBuildVersion(data.version);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [buildVersion]);

  const handleMeuPerfil = () => {
    setIsDropdownOpen(false);
    navigate("/meu-perfil");
  };

  const handleLogout = () => {
    setIsDropdownOpen(false);
    logout();
  };

  const handleValidarConexao = async () => {
    setIsDropdownOpen(false);
    if (!navigator.onLine) {
      toast.error("Sem conexão com a internet.");
      return;
    }
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      await fetch(window.location.origin + "/", {
        method: "HEAD",
        cache: "no-store",
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      toast.success("Conexão com a internet está OK.");
    } catch {
      toast.error("Falha ao validar conexão. Verifique sua internet.");
    }
  };

  const handleLimparCache = async () => {
    setIsDropdownOpen(false);
    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      }
      sessionStorage.clear();
      toast.success("Cache limpo. Recarregando...");
      window.location.href =
        window.location.origin +
        window.location.pathname +
        "?nocache=" +
        Date.now();
    } catch {
      toast.error("Erro ao limpar cache.");
    }
  };

  return (
    <div className="flex items-center space-x-3">
      <div className="relative">
        <button
          className="flex items-center gap-1.5 rounded-lg p-1 text-white/95 transition-all duration-200 hover:bg-white/10 hover:text-white"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-red-400">
            <span className="text-xs font-bold text-white">
              {user?.nome ? getInitials(user.nome) : "U"}
            </span>
          </div>
          <ChevronDown
            size={16}
            strokeWidth={2}
            className={`transition-transform duration-200 ${
              isDropdownOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {isDropdownOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsDropdownOpen(false)}
            />
            <div className="absolute right-0 top-full z-20 mt-1.5 w-56 rounded-lg border border-neutral-200 bg-white py-2 shadow-lg">
              <div className="border-b border-neutral-200 px-4 py-3">
                <p className="text-sm font-semibold text-neutral-900">
                  {user?.nome || "Usuário"}
                </p>
                <p className="mt-1 text-xs text-neutral-500">
                  {user?.role?.nome || "Sem permissão"}
                </p>
                {buildVersion && (
                  <p className="mt-1 text-[10px] text-neutral-400">
                    Frontend v{buildVersion}
                  </p>
                )}
              </div>
              <div className="py-1">
                <button
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-100"
                  onClick={handleMeuPerfil}
                >
                  <User size={16} />
                  Meu Perfil
                </button>
                <button
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-100"
                  onClick={handleValidarConexao}
                >
                  <Wifi size={16} />
                  Validar conexão
                </button>
                <button
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-100"
                  onClick={handleLimparCache}
                >
                  <Trash2 size={16} />
                  Limpar cache do site
                </button>
                <button
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-100"
                  onClick={handleLogout}
                >
                  <LogOut size={16} />
                  Sair
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UserActions;
